import {
  CloseOutlined,
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Avatar, Divider, Image } from 'antd';
import dayjs from 'dayjs';
import { Fragment, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { assetsApi, inquiryFeeApi, itemInquiryMessageApi } from '../../../../../apis';
import {
  ItemInquiryMessages,
  ItemInternalMessages,
  ItemMessageGroup,
  ProposeInquiryFeeDtoTypeEnum,
} from '../../../../../apis/client-axios';
import { RootState } from '../../../../../store';
import { QUERY_KEY, TIME_FORMAT } from '../../../../../util/constants';
import { Helper } from '../../../../../util/helper';
import { useIntl } from 'react-intl';
import { socketConfig } from '../../../../../webSocket/webSocket.config';
import { ChannelRegister } from '../../../../../webSocket/webSocket.constants';
import { ChatItemContext } from '..';
import { ComposerFile, InquiryThreadCard } from './InquiryThreadCard';
import { FEE_STATUS, ThreadTimeline } from './inquiryFeeTypes';
import { getInquiryOption } from './inquiryOptions';

export interface InquiryTabProps {
  listMessage: ItemInquiryMessages[];
  setListMesage: React.Dispatch<React.SetStateAction<ItemInquiryMessages[] | [] | ItemInternalMessages[]>>;
  totalReponse: number;
  setTotalResponse: React.Dispatch<React.SetStateAction<number>>;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  size: number;
  setSkipLoad: React.Dispatch<React.SetStateAction<boolean>>;
  skipLoad: boolean;
  setFileUploads: React.Dispatch<React.SetStateAction<{ privew: string; source: File }[] | undefined>>;
  fileUploads: { privew: string; source: File }[] | undefined;
  scrollToEnd: boolean;
  groupSelect: ItemMessageGroup | undefined;
}
export const InquiryTab = () => {
  const {
    listMessage,
    setListMesage,
    totalReponse,
    setTotalResponse,
    page,
    setPage,
    size,
    skipLoad,
    setSkipLoad,
    fileUploads,
    setFileUploads,
    scrollToEnd,
    groupSelect,
    staffById,
  } = useContext(ChatItemContext);
  const intl = useIntl();
  const { id } = useParams();
  const dummy = useRef(null);
  const { authUser } = useSelector((state: RootState) => state.auth);
  const [visibleSender, setVisibleSender] = useState(false);
  const [previewSender, setPreviewSender] = useState<{ source: string; index: number } | undefined>(undefined);
  const [visibleReceiver, setVisibleReceiver] = useState(false);
  const [previewReceiver, setPreviewReceiver] = useState<{ source: string; index: number } | undefined>(undefined);
  const { data, isLoading } = useQuery({
    queryKey: [QUERY_KEY.LIST_GROUP_ITEM_MESSAGE_INQUIRY, page],
    queryFn: () =>
      itemInquiryMessageApi.itemInquiryControllerGetAll(groupSelect?.id, page, size).then((res: any) => {
        let response: any = res;
        if (!!(res?.data as any)?.content?.length) {
          response.data.content = (res?.data as any)?.content.reverse();
        }
        return response;
      }),
    enabled: !!groupSelect?.id && groupSelect?.type == 0 && authUser?.role != 'operation',
    staleTime: 1000,
  });

  // Danh sách thread (option) + timeline merge của item — hiển thị dạng card, giữ song song list phẳng cũ.
  const { data: threadData, refetch: refetchThreads } = useQuery({
    queryKey: ['LIST_INQUIRY_FEE_THREAD', id],
    queryFn: () => inquiryFeeApi.inquiryFeeControllerGetByStorageItem(+(id as string)).then((res: any) => res?.data),
    enabled: !!id && groupSelect?.type === 0 && authUser?.role !== 'operation',
    staleTime: 1000,
  });

  // Bóc mảng thread từ response (chịu được nhiều shape: array trực tiếp / {data} / {content}).
  const rawThreads: any[] = Array.isArray(threadData)
    ? threadData
    : Array.isArray((threadData as any)?.data)
      ? (threadData as any).data
      : Array.isArray((threadData as any)?.content)
        ? (threadData as any).content
        : [];

  // Chuẩn hoá + chỉ render card cho thread option (có optionType). Thread chung hiển ở list phẳng như cũ.
  const threads: ThreadTimeline[] = rawThreads
    .map((t: any): ThreadTimeline => {
      const optionType =
        t?.optionType ?? t?.option_type ?? t?.itemMessageGroup?.optionType ?? t?.itemMessageGroup?.option_type;
      return {
        itemMessageGroupId: t?.itemMessageGroupId ?? t?.item_message_group_id ?? t?.itemMessageGroup?.id ?? t?.id,
        optionType,
        status: t?.status ?? t?.itemMessageGroup?.status,
        timeline: Array.isArray(t?.timeline) ? t.timeline : [],
      };
    })
    .filter((t) => !!t?.optionType && !!t?.itemMessageGroupId);

  const proposeFee = useMutation({
    mutationFn: (dto: {
      itemMessageGroupId: number;
      type: ProposeInquiryFeeDtoTypeEnum;
      chargeAmount: number;
      note?: string;
    }) => inquiryFeeApi.inquiryFeeControllerPropose(dto),
    onSuccess: () => refetchThreads(),
  });

  // Báo giá lại: supersede quote PROPOSED cũ + tạo quote mới (PATCH admin/:id).
  // Không dùng propose lần 2 (BE chặn THREAD_ALREADY_HAS_OPEN_QUOTE).
  const reproposeFee = useMutation({
    mutationFn: (payload: { id: number; chargeAmount: number; note?: string }) =>
      inquiryFeeApi.inquiryFeeControllerReproposed(payload.id, {
        chargeAmount: payload.chargeAmount,
        note: payload.note,
      }),
    onSuccess: () => refetchThreads(),
  });

  // Hoàn thành công việc (chỉ khi fee đã PAID) — BE: POST admin/:id/complete.
  const completeFee = useMutation({
    mutationFn: (feeId: number) => inquiryFeeApi.inquiryFeeControllerComplete(feeId),
    onSuccess: () => refetchThreads(),
  });

  const handleThreadSend = async (thread: ThreadTimeline, text: string, files: ComposerFile[], price?: number) => {
    // Có nhập giá → báo giá trên thread option; note = text kèm theo.
    if (price != null && !Number.isNaN(price) && price > 0 && thread?.optionType) {
      // Nếu thread đã có quote đang mở (PROPOSED) → báo giá LẠI (repropose), tiền tính theo lần cuối.
      const openFee = thread.timeline.find((item) => item.kind === 'fee' && item.data?.status === FEE_STATUS.PROPOSED);
      if (openFee?.data?.id != null) {
        reproposeFee.mutate({ id: openFee.data.id, chargeAmount: price, note: text || undefined });
        return;
      }
      proposeFee.mutate({
        itemMessageGroupId: thread.itemMessageGroupId,
        type: thread.optionType as ProposeInquiryFeeDtoTypeEnum,
        chargeAmount: price,
        note: text || undefined,
      });
      return;
    }
    if (!text && !files.length) return;

    // Không có giá → gửi tin nhắn thường vào thread (upload ảnh/PDF trước nếu có).
    let listUpload: any[] = [];
    if (files.length) {
      listUpload = await Promise.all(
        files.map((f) =>
          assetsApi.assetControllerUploadFile(f.source, 'chat-support').then((rs: any) => ({
            source: rs?.data?.srouce || rs?.data?.preview,
            assetId: rs?.data?.id,
          })),
        ),
      );
    }
    socketConfig.emit(
      ChannelRegister.SEND_INQUIRY_MESSAGE,
      JSON.stringify({
        type: ChannelRegister.SEND_INQUIRY_MESSAGE,
        content: {
          itemMessageGroupId: thread.itemMessageGroupId,
          sendSide: 1,
          sendStaffId: authUser?.id,
          message: text,
          isClosedInquiryMessage: 0,
          itemMessageImages: listUpload,
        },
      }),
    );
    setTimeout(() => refetchThreads(), 500);
  };

  useEffect(() => {
    if (data?.data?.content) {
      if (page == 1) {
        setListMesage(data?.data?.content as ItemInquiryMessages[]);
      } else {
        if (!skipLoad) {
          const messages = [...(data?.data?.content as ItemInquiryMessages[]), ...listMessage];
          setListMesage(messages);
          setSkipLoad(true);
        }
      }
      setTotalResponse(data?.data?.content?.length || 0);
    }
  }, [data]);

  // useEffect(() => {
  //   if (dummy?.current && !isLoading && (page === 1 || scrollToEnd)) {
  //     document.getElementById('scrollMessage')?.scrollIntoView({ behavior: 'instant', block: 'end', inline: 'end' });
  //   }
  // }, [listMessage, dummy?.current, scrollToEnd]);
  useLayoutEffect(() => {
    if (isLoading) return;

    const host = document.getElementById('scroll-message'); // lưu ý: "scroll-message", không phải "scrollMessage"
    if (!host) return;

    if (page === 1 || scrollToEnd) {
      host.scrollTo({ top: host.scrollHeight, left: 0, behavior: 'auto' });

      requestAnimationFrame(() => {
        host.scrollTop = host.scrollHeight;
        host.scrollLeft = 0;
      });
    }
  }, [isLoading, page, scrollToEnd, listMessage.length]);

  // Trộn tin nhắn thuần (thread chung) với các thread option rồi sort theo thời gian,
  // để card thread nằm đúng vị trí chèn giữa dòng chat.
  type MergedRow =
    | { kind: 'message'; at?: string; item: ItemInquiryMessages }
    | { kind: 'thread'; at?: string; thread: ThreadTimeline };

  const mergedRows: MergedRow[] = [
    ...((listMessage as ItemInquiryMessages[])?.map(
      (item): MergedRow => ({ kind: 'message', at: item?.createdAt, item }),
    ) || []),
    ...threads.map(
      (thread): MergedRow => ({ kind: 'thread', at: thread?.timeline?.[0]?.at || (thread as any)?.createdAt, thread }),
    ),
  ].sort((a, b) => dayjs(a.at).valueOf() - dayjs(b.at).valueOf());

  return (
    <>
      {mergedRows.map((row) => {
        if (row.kind === 'thread') {
          const thread = row.thread;
          return (
            <InquiryThreadCard
              key={`thread-${thread.itemMessageGroupId}`}
              thread={thread}
              option={getInquiryOption(thread.optionType)}
              staffById={staffById}
              onSend={(text, files, price) => handleThreadSend(thread, text, files, price)}
              onComplete={(feeId) => completeFee.mutate(feeId)}
            />
          );
        }
        const item = row.item;
        return (
          <Fragment key={item?.id}>
            {!item?.isClosedInquiryMessage ? (
              +item?.sendSide === 0 ? (
                <div key={item?.id} className="item-detail_message_content_other">
                  <div className="item-detail_message_content_other_avatar">
                    {/* <img src={Helper.getSourceFile(authUser?.avatar?.source)} alt="alt.." /> */}
                    <Avatar size={32} src={Helper.getSourceFile(item?.sendUser?.avatar?.source)}></Avatar>
                  </div>
                  <div className="item-detail_message_content_other_text w-100">
                    {!!item?.itemMessageImages?.length && (
                      <div>
                        {/* <Avatar
                          size={32}
                          src={Helper?.getSourceFile(
                            item.sendUser?.avatar?.source || item.sendStaff?.avatar?.source,
                          )}
                        /> */}
                        <div className="d-flex flex-column">
                          <span className="item-detail_message-file">
                            <Image.PreviewGroup
                              preview={
                                visibleReceiver
                                  ? {
                                      rootClassName: `image-preview__container ${!!item?.itemMessageImages?.length && item?.itemMessageImages?.length < 2 ? 'image-preview__container-limit' : ''} `,
                                      onChange: (current, prev) => {
                                        setPreviewReceiver({
                                          source: item?.itemMessageImages[current]?.source,
                                          index: current,
                                        });
                                      },
                                      src: Helper.getSourceFile(previewReceiver?.source),
                                      toolbarRender: (
                                        _,
                                        {
                                          transform: { scale },
                                          actions: { onRotateLeft, onRotateRight, onZoomOut, onZoomIn, onClose },
                                          current,
                                        },
                                      ) => (
                                        <div className="ant-image-preview-operations">
                                          <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                            <RotateLeftOutlined onClick={onRotateLeft} />
                                          </div>
                                          <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                            <RotateRightOutlined onClick={onRotateRight} />
                                          </div>
                                          <div
                                            className={`ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut ${scale === 1 && 'ant-image-preview-operations-operation-disabled'}`}
                                          >
                                            <ZoomOutOutlined disabled={true} onClick={onZoomOut} />
                                          </div>
                                          <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                            <ZoomInOutlined disabled={scale === 50} onClick={onZoomIn} />
                                          </div>
                                          <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                            <Divider type="vertical" className="bg-white" />
                                          </div>
                                          <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                            <CloseOutlined
                                              disabled={scale === 50}
                                              onClick={() => {
                                                onClose();
                                                // setVisibleReceiver(false)
                                              }}
                                            />
                                          </div>
                                        </div>
                                      ),
                                    }
                                  : false
                              }
                            >
                              {item.itemMessageImages?.map((item, index) => {
                                return (
                                  <Image
                                    key={item?.id + index}
                                    onClick={() => {
                                      setVisibleReceiver(true);
                                      setPreviewReceiver({
                                        source: item?.source,
                                        index: +index + 1,
                                      });
                                    }}
                                    width={98}
                                    height={98}
                                    className="rounded"
                                    src={Helper.getSourceFile(item?.source)}
                                  />
                                );
                              })}
                            </Image.PreviewGroup>
                          </span>
                          {/* <div className="d-flex flex-column">
                            <span className="color-sub-title font-weight-600 font-size-12 m-t-2">
                              {item.sendUser?.name || item.sendStaff?.loginId || item.sendUser?.emailAddress}
                            </span>
                            <span className="color-sub-cancel font-size-10 m-t-2">
                              {dayjs(item.createdAt).format(TIME_FORMAT.MESSAGE_TIME)}
                            </span>
                          </div> */}
                        </div>
                      </div>
                    )}
                    <p className="item-detail_message_content_other_text_content m-t-2">{item?.message}</p>
                    <p className="item-detail_message_content_other_text_name m-t-2">
                      {item?.sendUser?.name || item?.sendUser?.emailAddress}
                    </p>
                    <p className="item-detail_message_content_other_text_time">
                      {' '}
                      {dayjs(item?.createdAt).format(TIME_FORMAT.MESSAGE_TIME)}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {item?.sendStaff?.role.includes(authUser?.role) ? (
                    <div
                      className={`item-detail_message_content_me ${item?.sendStaff?.role.includes(authUser?.role) && 'convert-me-send'}`}
                    >
                      <div
                        className={`item-detail_message_content_me_avatar ${item?.sendStaff?.role.includes(authUser?.role) && 'me-send'}`}
                      >
                        {/* <img src={Helper.getSourceFile(authUser?.avatar?.source)} alt="alt.." /> */}
                        <Avatar size={32} src={Helper.getSourceFile(authUser?.avatar?.source)}></Avatar>
                      </div>
                      <div className="item-detail_message_content_me_text w-100">
                        {!!item?.itemMessageImages?.length && (
                          <div>
                            <div className="d-flex flex-column align-items-end">
                              <span className="item-detail_message-file">
                                <Image.PreviewGroup
                                  preview={
                                    visibleSender
                                      ? {
                                          rootClassName: `image-preview__container ${!!item?.itemMessageImages?.length && item?.itemMessageImages?.length < 2 ? 'image-preview__container-limit' : ''} `,
                                          onChange: (current, prev) => {
                                            setPreviewSender({
                                              source: item?.itemMessageImages[current]?.source,
                                              index: current,
                                            });
                                          },
                                          src: Helper.getSourceFile(previewSender?.source),
                                          toolbarRender: (
                                            _,
                                            {
                                              transform: { scale },
                                              actions: { onRotateLeft, onRotateRight, onZoomOut, onZoomIn, onClose },
                                            },
                                          ) => (
                                            <div className="ant-image-preview-operations">
                                              <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                                <RotateLeftOutlined onClick={onRotateLeft} />
                                              </div>
                                              <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                                <RotateRightOutlined onClick={onRotateRight} />
                                              </div>
                                              <div
                                                className={`ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut ${scale === 1 && 'ant-image-preview-operations-operation-disabled'}`}
                                              >
                                                <ZoomOutOutlined disabled={true} onClick={onZoomOut} />
                                              </div>
                                              <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                                <ZoomInOutlined disabled={scale === 50} onClick={onZoomIn} />
                                              </div>
                                              <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut">
                                                <Divider type="vertical" className="bg-white" />
                                              </div>
                                              <div className="ant-image-preview-operations-operation ant-image-preview-operations-operation-zoomOut-close">
                                                <CloseOutlined
                                                  disabled={scale === 50}
                                                  onClick={() => {
                                                    // setVisibleSender(false);
                                                    onClose();
                                                  }}
                                                />
                                              </div>
                                            </div>
                                          ),
                                        }
                                      : false
                                  }
                                >
                                  {item?.itemMessageImages?.map((item, index) => {
                                    return (
                                      <Image
                                        key={item?.id + index}
                                        onClick={() => {
                                          setVisibleSender(true);
                                          setPreviewSender({
                                            source: item?.source,
                                            index: +index + 1,
                                          });
                                        }}
                                        width={98}
                                        height={98}
                                        className="rounded"
                                        src={Helper.getSourceFile(item?.source)}
                                      />
                                    );
                                  })}
                                </Image.PreviewGroup>
                              </span>
                              {/* <span className="color-sub-title font-weight-600 font-size-12 m-t-2">
                                {item?.sendStaff?.name || item?.sendStaff?.loginId}
                              </span>
                              <span className="color-sub-cancel font-size-10 m-t-2">
                                {dayjs(item?.createdAt).format(TIME_FORMAT.MESSAGE_TIME)}
                              </span> */}
                            </div>
                          </div>
                        )}
                        <div className="d-flex justify-content-end">
                          <p className="item-detail_message_content_me_text_content m-t-2">{item?.message}</p>
                        </div>
                        <p
                          className={`item-detail_message_content_me_text_name m-t-2 ${item?.sendStaff?.role.includes(authUser?.role) && 'me-send'}`}
                        >
                          {item?.sendStaff?.name || item?.sendStaff?.loginId}
                        </p>
                        <p
                          className={`item-detail_message_content_me_text_time ${item?.sendStaff?.role.includes(authUser?.role) && 'me-send'}`}
                        >
                          {dayjs(item?.createdAt).format(TIME_FORMAT.MESSAGE_TIME)}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`item-detail_message_content_me ${item?.sendStaff?.role.includes(authUser?.role) && 'convert-me-send'}`}
                    >
                      <div className="item-detail_message_content_me_avatar">
                        {/* <img src="/assets/icons/chatsupport.png" alt="" /> */}
                        <Avatar size={32} src={Helper?.getSourceFile(item.sendStaff?.avatar?.source)} />
                      </div>
                      <div className="item-detail_message_content_me_text w-100">
                        <p className="item-detail_message_content_me_text_content-other m-t-2">{item?.message}</p>
                        <p
                          className={`item-detail_message_content_me_text_name m-t-2 ${item?.sendStaff?.role.includes(authUser?.role) && 'me-send'}`}
                        >
                          {item?.sendStaff?.name || item?.sendStaff?.loginId}
                        </p>
                        <p
                          className={`item-detail_message_content_me_text_time ${item?.sendStaff?.role.includes(authUser?.role) && 'me-send'}`}
                        >
                          {dayjs(item?.createdAt).format(TIME_FORMAT.MESSAGE_TIME)}
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )
            ) : (
              <div className="item-detail_message_content_seen">
                <p>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M4.125 13.125L9.375 18.375L19.875 7.125"
                      stroke="#514B4D"
                      strokeWidth="1.875"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>{intl.formatMessage({ id: 'chat-item.inquiry.closed' })}</span>
                </p>
              </div>
            )}
          </Fragment>
        );
      })}
      <div className="p-t-12" ref={dummy} id="scrollMessage" style={{ height: '1px' }}></div>
    </>
  );
};
